import React from "react";
import { NavLink, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import { useTheme } from "@/lib/theme";
import AlertPoller from "@/components/AlertPoller";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import LiveStatusBadge from "@/components/LiveStatusBadge";
import LiveClock from "@/components/LiveClock";
import {
  LogOut, Languages, Moon, Sun, ChevronDown, ChevronRight,
  Sliders,
} from "lucide-react";
import { api } from "@/lib/api";
import {
  MODULE_REGISTRY, DEFAULT_GROUPS, assembleNav, findParentGidForRoute,
  defaultAssembled, applyUserPrefs,
} from "@/lib/navigation";
import MenuUserPrefsModal from "@/components/MenuUserPrefsModal";
import GlobalSearch from "@/components/GlobalSearch";

// Backward-compat re-exports so existing imports (canAccess/RouteGuard/MODULE_KEYS)
// continue to work everywhere.
export const MODULE_KEYS = MODULE_REGISTRY.map((m) => ({
  key: m.permKey, label: m.defaultLabel,
}));

export function canAccess(user, key) {
  if (!user) return false;
  if (user.role === "admin") return true;
  if (key === null || key === undefined) return true;   // public modules
  const mp = user.module_permissions || {};
  return mp[key] === true;
}

export function RouteGuard({ moduleKey, children }) {
  const { user } = useAuth();
  if (!canAccess(user, moduleKey)) return <Navigate to="/" replace />;
  return children;
}

// ─── Persistent open-state for accordion groups (user-scoped UI pref) ───
const OPEN_KEY = "mp_erp_nav_open_v1";
function loadOpenGroups() {
  try {
    const raw = localStorage.getItem(OPEN_KEY);
    if (!raw) return null;
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : null;
  } catch { return null; }
}
function saveOpenGroups(set) {
  try { localStorage.setItem(OPEN_KEY, JSON.stringify(Array.from(set))); }
  catch {}
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { locale, setLocale, t } = useT();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const [csBadge, setCsBadge] = React.useState(0);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  React.useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  // Menu config from server + per-user prefs (overlay)
  const [globalAssembled, setGlobalAssembled] = React.useState(() => defaultAssembled());
  const [userPrefs, setUserPrefs] = React.useState({ hidden_ids: [], item_order: {}, custom_parent: {} });
  const [prefsOpen, setPrefsOpen] = React.useState(false);

  React.useEffect(() => {
    let alive = true;
    Promise.all([
      api.get("/menu/config").catch(() => null),
      api.get("/menu/user-prefs").catch(() => ({ hidden_ids: [], item_order: {}, custom_parent: {} })),
    ]).then(([cfg, prefs]) => {
      if (!alive) return;
      if (cfg?.config) setGlobalAssembled(assembleNav(cfg.config));
      if (prefs) setUserPrefs({
        hidden_ids: prefs.hidden_ids || [],
        item_order: prefs.item_order || {},
        custom_parent: prefs.custom_parent || {},
      });
    });
    return () => { alive = false; };
  }, []);

  // Reload menu config when it's mutated from Settings page (custom event)
  React.useEffect(() => {
    const onUpdate = () => {
      api.get("/menu/config")
        .then((r) => { if (r?.config) setGlobalAssembled(assembleNav(r.config)); })
        .catch(() => {});
    };
    window.addEventListener("menu:config-updated", onUpdate);
    return () => window.removeEventListener("menu:config-updated", onUpdate);
  }, []);

  const assembled = React.useMemo(
    () => applyUserPrefs(globalAssembled, userPrefs),
    [globalAssembled, userPrefs],
  );

  // Filter items by permission
  const visibleAssembled = React.useMemo(() => {
    return assembled
      .map((g) => ({
        ...g,
        items: g.items.filter((it) => canAccess(user, it.permKey)),
      }))
      .filter((g) => g.items.length > 0);
  }, [assembled, user]);

  // Accordion open state (bağımsız, multi-open, refresh-persist)
  const [openGroups, setOpenGroups] = React.useState(() => {
    const saved = loadOpenGroups();
    if (saved) return saved;
    // Default: open groups that have the current active route
    return new Set(["genel"]);
  });

  const setOpenAndPersist = React.useCallback((updater) => {
    setOpenGroups((prev) => {
      const next = new Set(updater(prev));
      saveOpenGroups(next);
      return next;
    });
  }, []);

  // Active route → auto-expand the parent (without closing others)
  React.useEffect(() => {
    const gid = findParentGidForRoute(visibleAssembled, location.pathname);
    if (gid) {
      setOpenGroups((prev) => {
        if (prev.has(gid)) return prev;
        const next = new Set(prev); next.add(gid);
        saveOpenGroups(next);
        return next;
      });
    }
  }, [location.pathname, visibleAssembled]);

  const toggleGroup = (gid) => {
    setOpenAndPersist((prev) => {
      const next = new Set(prev);
      if (next.has(gid)) next.delete(gid); else next.add(gid);
      return next;
    });
  };

  // CS badge — unchanged from previous Layout
  React.useEffect(() => {
    if (!user) return;
    let alive = true;
    const load = async () => {
      try { const r = await api.get("/customer-service/badge"); if (alive) setCsBadge(r.waiting_customers || 0); } catch (e) {}
    };
    load();
    const iv = setInterval(load, 15000);
    return () => { alive = false; clearInterval(iv); };
  }, [user, location.pathname]);

  return (
    <div className="min-h-screen text-slate-900 dark:text-slate-100" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      {/* Mobile overlay (≤768px) — desktop unchanged */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/50 backdrop-blur-sm md:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="mobile-sidebar-overlay"
        />
      )}
      <aside
        data-testid="app-sidebar"
        className={`w-64 bg-white/95 dark:bg-slate-900/95 backdrop-blur-sm border-r border-slate-200/60 dark:border-slate-700/50 flex flex-col fixed top-0 left-0 h-screen z-50 md:z-40 transform transition-transform duration-200 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } md:translate-x-0`}
      >
        <div className="h-16 px-4 flex items-center border-b border-slate-200/60 dark:border-slate-700/50">
          <img src="/logo.png" alt="Meta Pharma ERP" data-testid="sidebar-logo" className="h-12 w-12 object-contain shrink-0" />
          <div className="ml-2 min-w-0 flex-1">
            <div className="text-sm font-semibold tracking-tight text-slate-800 dark:text-slate-100 truncate" style={{ fontFamily: "'Manrope', sans-serif" }}>Meta Pharma ERP</div>
            <div className="text-[10px] text-slate-400 dark:text-slate-500 uppercase tracking-wider">{t("Fason Üretim")}</div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="md:hidden ml-2 p-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500"
            data-testid="mobile-sidebar-close"
            aria-label="Menüyü kapat"
          >✕</button>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto py-3" data-testid="sidebar-nav-scroll">
          {visibleAssembled.map((g) => {
            const isTop = g.gid === "__top__";
            const isOpen = isTop ? true : openGroups.has(g.gid);
            const GroupIcon = g.icon || DEFAULT_GROUPS[0].icon;
            const containsActive = g.items.some((it) =>
              it.to === "/" ? location.pathname === "/" : location.pathname.startsWith(it.to)
            );
            if (g.items.length === 0) return null;
            return (
              <div key={g.gid} className="mb-1">
                {!isTop && (
                  <button
                    type="button"
                    onClick={() => toggleGroup(g.gid)}
                    data-testid={`nav-group-${g.gid}`}
                    aria-expanded={isOpen}
                    className={`w-full flex items-center gap-2 px-3 py-1.5 mx-2 rounded-md text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                      containsActive
                        ? "text-indigo-700 dark:text-indigo-300"
                        : "text-slate-500 dark:text-slate-400"
                    } hover:bg-slate-100 dark:hover:bg-slate-800`}
                  >
                    {isOpen
                      ? <ChevronDown size={12} className="shrink-0"/>
                      : <ChevronRight size={12} className="shrink-0"/>}
                    <GroupIcon size={13} className="shrink-0"/>
                    <span className="truncate flex-1 text-left">{g.displayLabel}</span>
                  </button>
                )}
                {isOpen && (
                  <div className={isTop ? "" : "mt-0.5"} data-testid={`nav-group-${g.gid}-items`}>
                    {g.items.map((it) => {
                      const Icon = it.icon;
                      const ac = it.accent || "indigo";
                      const pad = isTop ? "pl-3" : "pl-6";
                      return (
                        <NavLink
                          key={it.id}
                          to={it.to}
                          end={it.to === "/"}
                          data-testid={`nav-${it.id}`}
                          className={({ isActive }) =>
                            `flex items-center gap-2.5 ${pad} pr-3 py-1.5 text-[13px] mx-2 my-0.5 rounded-md transition-all border ${
                              isActive
                                ? `bg-${ac}-100 dark:bg-${ac}-500/20 text-${ac}-800 dark:text-${ac}-100 font-semibold shadow-sm border-${ac}-300 dark:border-${ac}-500/60`
                                : `bg-transparent text-slate-700 dark:text-slate-300 border-transparent hover:bg-${ac}-50 dark:hover:bg-${ac}-500/10 hover:text-${ac}-700 dark:hover:text-${ac}-200`
                            }`
                          }
                        >
                          <Icon size={14} className={`text-${ac}-500 dark:text-${ac}-400 shrink-0`} />
                          <span className="truncate flex-1">{t(it.displayLabel)}</span>
                          {it.permKey === "customer_service" && csBadge > 0 && (
                            <span className="inline-flex items-center justify-center min-w-[18px] h-4 px-1 text-[10px] font-bold rounded-full bg-red-500 text-white animate-pulse" data-testid="cs-badge">
                              {csBadge}
                            </span>
                          )}
                        </NavLink>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="border-t border-slate-200/60 dark:border-slate-700/50 p-3 sticky bottom-0 bg-white/95 dark:bg-slate-900/95">
          <div className="text-xs">
            <div className="font-medium text-slate-800 dark:text-slate-100 truncate">{user?.name}</div>
            <div className="text-slate-500 dark:text-slate-400 truncate">{user?.email}</div>
            <div className="text-[10px] mt-0.5 text-indigo-600 dark:text-indigo-400 uppercase tracking-wide">{user?.role}</div>
          </div>
          <div className="mt-2 flex gap-1">
            <button
              onClick={() => setPrefsOpen(true)}
              data-testid="menu-personalize-btn"
              className="flex-1 flex items-center justify-center gap-1.5 text-xs px-2 py-1.5 rounded-md border border-indigo-200 dark:border-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-900/40 text-indigo-700 dark:text-indigo-300"
              title="Kendi menü görünümünüzü kişiselleştirin"
            >
              <Sliders size={13}/> Menümü Kişiselleştir
            </button>
          </div>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="mt-2 w-full flex items-center justify-center gap-2 text-xs px-3 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300"
          >
            <LogOut size={14} /> {t("Çıkış")}
          </button>
        </div>
      </aside>
      {prefsOpen && (
        <MenuUserPrefsModal
          open={prefsOpen}
          onClose={() => setPrefsOpen(false)}
          assembled={globalAssembled}
          initialPrefs={userPrefs}
          onSaved={(next) => setUserPrefs(next)}
        />
      )}
      <main className="flex-1 min-w-0 md:ml-64">
        <div className="h-14 sticky top-0 z-30 bg-white/85 dark:bg-slate-900/70 backdrop-blur border-b border-slate-200/60 dark:border-slate-700/50 flex items-center px-3 md:px-6 gap-2 md:gap-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="md:hidden p-2 -ml-1 rounded hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300"
            data-testid="mobile-hamburger"
            aria-label="Menüyü aç"
          >
            <span className="block w-5 h-0.5 bg-current mb-1"/>
            <span className="block w-5 h-0.5 bg-current mb-1"/>
            <span className="block w-5 h-0.5 bg-current"/>
          </button>
          <div className="flex-1 sm:hidden"/>
          <GlobalSearch />
          <button
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            className="inline-flex items-center gap-1 border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/70 rounded-md p-1.5 hover:bg-white dark:hover:bg-slate-700 transition"
            title={theme === "dark" ? "Aydınlık moda geç" : "Karanlık moda geç"}
            data-testid="theme-toggle"
          >
            {theme === "dark" ? <Sun size={14} className="text-amber-300"/> : <Moon size={14} className="text-indigo-500"/>}
          </button>
          <div className="inline-flex items-center gap-1 border border-slate-200 dark:border-slate-700 rounded-md bg-slate-50 dark:bg-slate-800/70 p-0.5" data-testid="locale-toggle">
            <Languages size={13} className="text-slate-400 ml-1"/>
            <button onClick={() => setLocale("tr")} data-testid="locale-tr"
              className={`text-[11px] font-semibold px-2 py-0.5 rounded ${locale === "tr" ? "bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100"}`}>TR</button>
            <button onClick={() => setLocale("en")} data-testid="locale-en"
              className={`text-[11px] font-semibold px-2 py-0.5 rounded ${locale === "en" ? "bg-white dark:bg-slate-700 text-indigo-700 dark:text-indigo-300 shadow-sm" : "text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-100"}`}>EN</button>
          </div>
          <LiveClock />
          <LiveStatusBadge />
          <AlertPoller />
        </div>
        <div className="p-3 md:p-6 max-w-[1600px]">{children}</div>
      </main>
      <PWAInstallPrompt />
    </div>
  );
}
